import { Spacer } from "@hackthenorth/north";
import {
  MagnifyingGlassIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import { Button, Table, Text, TextInput } from "north.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Icon, PageBar } from "src/shared/components";
import Header from "src/shared/components/Header";
import PageWrapper from "src/shared/components/PageWrapper";
import { deviceBreakpoints } from "src/theme/deviceBreakpoints";
import {
  formatVolunteerShiftDay,
  getVolunteerShiftHourAndMinute,
} from "src/views/volunteer/shared/utils/date";

import "twin.macro";
import MentorShiftModal from "../shared/components/MentorShiftModal";
import { useDetailedMentorShiftsQuery } from "../shift-schedule/graphql/detailedMentorShifts.generated";
import { parseMaxCountRestriction } from "../shift-schedule/utils";

const ShiftsPage: React.FC = () => {
  const [page, setPage] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [search, setSearch] = useState<string>("");
  const [selectedShift, setSelectedShift] = useState<number | null>(null);
  const [viewModal, setViewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  const { data: mentorShiftsData, refetch: refetchMentorShifts } =
    useDetailedMentorShiftsQuery({
      variables: {
        where: {},
      },
    });

  const refetchAllShifts = useCallback(() => {
    refetchMentorShifts();
  }, [refetchMentorShifts]);

  const columns = [
    {
      id: "shift",
      name: "shift",
    },
    {
      id: "dateTime",
      name: "date/time",
    },
    {
      id: "location",
      name: "location",
    },
    {
      id: "max",
      name: "required mentors",
    },
    {
      id: "currentlySignedUp",
      name: "currently signed up",
    },
    {
      id: "actions",
      name: "actions",
      hideLabel: true,
    },
  ];

  const data = useMemo(() => {
    if (!mentorShiftsData?.detailedMentorShifts) {
      return [];
    }

    const sortedShifts = [
      ...(mentorShiftsData?.detailedMentorShifts ?? []),
    ].sort((a, b) => moment(a.start_time).diff(moment(b.start_time)));

    return (
      sortedShifts.map((shift) => {
        const count = shift.shift_signups?.length ?? 0;

        return {
          shift: shift.name,
          dateTime: `${
            formatVolunteerShiftDay(new Date(shift.start_time)) ?? ""
          }, ${getVolunteerShiftHourAndMinute(
            new Date(shift.start_time)
          )} - ${getVolunteerShiftHourAndMinute(new Date(shift.end_time))}`,
          location: shift.location ?? "",
          max: parseMaxCountRestriction(shift.restrictions),
          currentlySignedUp: count,
          mentorsSignedUp: shift.shift_signups
            ?.map((mentor) => mentor?.event_attendee.user.name)
            .join(", "),
          actions: (
            <Button
              size="sm"
              color="secondary"
              onClick={() => {
                setSelectedShift(shift.id);
                setViewModal(true);
              }}
            >
              <Text tw="font-bold">View</Text>
            </Button>
          ),
        };
      }) ?? []
    );
  }, [mentorShiftsData]);

  const filteredData = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.filter((item) => {
      return (
        item.shift.toLowerCase().includes(search.toLowerCase()) ||
        item.dateTime.toLowerCase().includes(search.toLowerCase()) ||
        item.location.toLowerCase().includes(search.toLowerCase()) ||
        item.currentlySignedUp.toString().includes(search.toLowerCase())
      );
    });
  }, [data, search]);

  useEffect(() => {
    refetchMentorShifts();
  }, [refetchMentorShifts]);

  useEffect(() => {
    if (viewModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [viewModal]);

  const handleDownloadCSV = () => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]).filter(
      (key) => key !== "actions" && key !== "max" && key !== "currentlySignedUp"
    );
    const csvRows = [
      [...headers].join(","),
      ...data.map((row) =>
        [...headers.map((header) => `"${(row as any)[header]}"`)].join(",")
      ),
    ];

    const csvContent = csvRows.join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "mentor_shifts.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  return (
    <>
      {selectedShift && (
        <MentorShiftModal
          shiftId={selectedShift}
          open={viewModal}
          setOpen={setViewModal}
          onDeleteShift={() => {
            setViewModal(false);
            refetchMentorShifts();
          }}
          onChange={() => {
            refetchAllShifts();
          }}
        />
      )}

      <Header
        title="Mentor Shifts"
        subtitle="View, create, edit, delete, and assign shifts!"
        actionButton={
          <Button
            leadingIcon={
              <Icon name="spon-download" size="14px" color="transparent" />
            }
            color="primary"
            onClick={handleDownloadCSV}
          >
            Download CSV
          </Button>
        }
      />

      <PageWrapper>
        <ControlsContainer>
          <Searchbar
            type="text"
            value={search}
            placeholder="Search for a shift..."
            size="sm"
            leadingIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
            onChange={(e) => {
              setPage(0);
              setSearch(e.target.value);
            }}
          />

          <AddContainer>
            {/* <Button
              leadingIcon={<UploadIcon width={12.8} color="grey" />}
              size="md"
              color="secondary"
              onClick={() => navigate("upload")}
            >
              Upload from CSV
            </Button> */}
            <Button
              leadingIcon={<PlusCircleIcon width={12.8} color="white" />}
              size="md"
              color="primary"
              onClick={() => navigate("add")}
            >
              Add a shift
            </Button>
          </AddContainer>
        </ControlsContainer>

        <Spacer height={16} />

        <Table
          displayStyle="striped"
          itemsPerPage={itemsPerPage}
          page={page}
          showFooter
          columns={columns}
          data={filteredData}
        />
        <PageBar
          page={page}
          itemsPerPage={itemsPerPage}
          totalItems={filteredData.length}
          onPageChange={setPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </PageWrapper>
    </>
  );
};

const Searchbar = styled(TextInput)`
  width: 100%;
`;

const AddContainer = styled.div`
  display: flex;
  flex-wrap: wrap;

  > * {
    flex: 1 1 140px;
  }
  flex-direction: row;
  gap: 16px;
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: 16px;

  @media (max-width: ${deviceBreakpoints.tablet}px) {
    flex-direction: column;
  }
`;

export default ShiftsPage;
